import { useEffect, useRef, useState } from 'react';

import { RequestStatus } from '../types';

interface UseImagePreloadOptions {
  enabled?: boolean;
  cache?: React.RefObject<Record<string, boolean>>;
  onLoad?: (event: Event) => void;
}

/** Предзагружает изображение через `new Image()` и возвращает статус загрузки. */
export const useImagePreload = (
  src?: string,
  options?: UseImagePreloadOptions,
): RequestStatus => {
  const [status, setStatus] = useState<RequestStatus>('idle');
  const abortControllerRef = useRef<AbortController | null>(null);
  const onLoadRef = useRef(options?.onLoad);
  const { enabled, cache } = options ?? {};

  useEffect(() => {
    onLoadRef.current = options?.onLoad;
  }, [options?.onLoad]);

  useEffect(() => {
    if (!src || enabled === false) {
      setStatus('idle');
      return;
    }

    if (cache?.current[src]) {
      setStatus('complete');
      return;
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    const img = new Image();

    img.onloadstart = () => {
      if (signal.aborted) return;
      setStatus('loading');
    };

    img.onload = (event) => {
      if (signal.aborted) return;
      setStatus('complete');
      onLoadRef.current?.(event);
    };

    img.onerror = () => {
      if (signal.aborted) return;
      setStatus('failed');
    };

    img.src = src;

    return () => {
      abortControllerRef.current?.abort();
      img.onloadstart = null;
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [src, enabled, cache]);

  return status;
};
